<script setup>
import SmartBox from '../common/SmartBox.vue';
import SearchInput from './components/SearchInput.vue';
import SearchEmpty from './components/SearchEmpty.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import { ref, computed, getCurrentInstance, onMounted, nextTick } from 'vue';

const { proxy } = getCurrentInstance()
const __ = proxy.__

const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const settings = window.__pt.settings;
const MAX_SEARCH_RESULTS = 10;
const defaultDate = fn.getDate();
const format = window.__pt.settings.formatBoxDate;

const RETURN_MODES = {
  SAME: 'same',
  OTHER: 'other',
};

const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, '0');
  const minutes = i % 2 === 0 ? '00' : '30';
  return `${hours}:${minutes}`;
});

const datesBox = ref(null);
const isMobile = ref(false);
const openPickupOfficeSearch = ref(false);
const loadingPickupOffices = ref(false);
const openReturnOfficeSearch = ref(false);
const loadingReturnOffices = ref(false);
const pickupOfficeMobileInput = ref(null);
const returnOfficeMobileInput = ref(null);

const form = ref({
  returnMode: RETURN_MODES.SAME,
  pickupOffice: '',
  returnOffice: '',
  dates: '',
  pickupTime: '10:00',
  returnTime: '10:00',
});

const fieldErrors = ref({
  pickupOffice: '',
  returnOffice: '',
  dates: '',
});

const isOtherReturnPlace = computed(() => form.value.returnMode === RETURN_MODES.OTHER);

function focusMobileInput(inputRef) {
  if (!isMobile.value) return;

  nextTick(() => {
    inputRef.value?.focus();
  });
}

function onOpenPickupOfficeSearch() {
  focusMobileInput(pickupOfficeMobileInput);
}

function onOpenReturnOfficeSearch() {
  focusMobileInput(returnOfficeMobileInput);
}

const checkIn = ref(null);
const checkOut = ref(null);

function initializeDefaultDates() {
  const prequote = fn.mobileAndTabletCheck()
    ? config.configDaysPrequoteMobile
    : config.configDaysPrequoteDesktop;
  const startDate = new Date(defaultDate);

  startDate.setDate(startDate.getDate() + prequote.weeksPrequote * 7);
  startDate.setDate(startDate.getDate() - startDate.getDay() + prequote.startDate);

  checkIn.value = fn.getDate(fn.formatDate(startDate, settings.formatDate));
  checkOut.value = fn.getDate(fn.formatDate(startDate, settings.formatDate));
  checkOut.value.setDate(checkOut.value.getDate() + prequote.daysPrequote);
}

const calendarDates = computed({
  get() {
    return {
      start: checkIn.value ? new Date(checkIn.value) : null,
      end: checkOut.value ? new Date(checkOut.value) : null
    };
  },
  set(val) {
    checkIn.value = val.start ? new Date(val.start) : null;
    checkOut.value = val.end ? new Date(val.end) : null;
    updateFormDates();
  }
});

function updateFormDates() {
  if (!checkIn.value || !checkOut.value) return;
  const start = dayjs(checkIn.value).format(format).replace(/\./g, '');
  const end = dayjs(checkOut.value).format(format).replace(/\./g, '');
  form.value.dates = `${start} - ${end}`;
}

function onSelectDates(val) {
  checkIn.value = val.start;
  checkOut.value = val.end;
  updateFormDates();
  if (datesBox.value) datesBox.value.close();
}

function onChangeReturnMode() {
  if (form.value.returnMode === RETURN_MODES.SAME) {
    form.value.returnOffice = '';
    selectedReturnOffice.value = null;
    returnOfficeResults.value = [];
    fieldErrors.value.returnOffice = '';
  }
}

// Office autofill isn't configured yet — endpoint wiring pending.
const pickupOfficeResults = ref([]);
const returnOfficeResults = ref([]);
const selectedPickupOffice = ref(null);
const selectedReturnOffice = ref(null);
const searchedPickupOffice = ref(false);
const searchedReturnOffice = ref(false);

const showEmptyPickupOffice = computed(() => searchedPickupOffice.value && !loadingPickupOffices.value && pickupOfficeResults.value.length === 0);
const showEmptyReturnOffice = computed(() => searchedReturnOffice.value && !loadingReturnOffices.value && returnOfficeResults.value.length === 0);

function getOfficeLabel(item) {
  if (typeof item === 'string') return item;
  return item?.display || item?.displayName || item?.name || item?.officeName || item?.locationName || item?.description || '';
}

async function onSearchPickupOffice(value, mobile = false) {
  form.value.pickupOffice = value;
  fieldErrors.value.pickupOffice = '';
  selectedPickupOffice.value = null;

  if (!value || value.trim().length < 3) {
    pickupOfficeResults.value = [];
    searchedPickupOffice.value = false;
    if (!mobile) openPickupOfficeSearch.value = false;
    return;
  }

  loadingPickupOffices.value = true;
  if (!mobile) openPickupOfficeSearch.value = false;

  try {
    const response = await axios.get(config.endPoints.carsOfficesUrl, {
      params: { Query: value }
    });
    const data = response?.data;
    const results = Array.isArray(data) ? data : data?.results || data?.items || [];
    pickupOfficeResults.value = results.slice(0, MAX_SEARCH_RESULTS);
    openPickupOfficeSearch.value = true;
  } catch (error) {
    console.log(error);
    pickupOfficeResults.value = [];
    if (!mobile) openPickupOfficeSearch.value = false;
  } finally {
    loadingPickupOffices.value = false;
    searchedPickupOffice.value = true;
  }
}

function onSelectPickupOffice(item) {
  selectedPickupOffice.value = item;
  form.value.pickupOffice = getOfficeLabel(item);
  openPickupOfficeSearch.value = false;
}

async function onSearchReturnOffice(value, mobile = false) {
  form.value.returnOffice = value;
  fieldErrors.value.returnOffice = '';
  selectedReturnOffice.value = null;

  if (!value || value.trim().length < 3) {
    returnOfficeResults.value = [];
    searchedReturnOffice.value = false;
    if (!mobile) openReturnOfficeSearch.value = false;
    return;
  }

  loadingReturnOffices.value = true;
  if (!mobile) openReturnOfficeSearch.value = false;

  try {
    const response = await axios.get(config.endPoints.carsOfficesUrl, {
      params: { Query: value }
    });
    const data = response?.data;
    const results = Array.isArray(data) ? data : data?.results || data?.items || [];
    returnOfficeResults.value = results.slice(0, MAX_SEARCH_RESULTS);
    openReturnOfficeSearch.value = true;
  } catch (error) {
    console.log(error);
    returnOfficeResults.value = [];
    if (!mobile) openReturnOfficeSearch.value = false;
  } finally {
    loadingReturnOffices.value = false;
    searchedReturnOffice.value = true;
  }
}

function onSelectReturnOffice(item) {
  selectedReturnOffice.value = item;
  form.value.returnOffice = getOfficeLabel(item);
  openReturnOfficeSearch.value = false;
}

const submitting = ref(false);
const submitError = ref('');

function validateForm() {
  fieldErrors.value.pickupOffice = selectedPickupOffice.value ? '' : __('booker.select_pickup_office');
  fieldErrors.value.returnOffice = (!isOtherReturnPlace.value || selectedReturnOffice.value) ? '' : __('booker.select_return_office');
  fieldErrors.value.dates = (checkIn.value && checkOut.value) ? '' : __('booker.select_dates');

  return !fieldErrors.value.pickupOffice && !fieldErrors.value.returnOffice && !fieldErrors.value.dates;
}

function getOfficeId(item) {
  return item?.id ?? item?.stationId ?? '';
}

function getOfficeExternalId(item) {
  return item?.externalId ?? item?.externalID ?? '';
}

function getOfficeType(item) {
  return item?.type ?? item?.stationType ?? 1;
}

function formatWidgetTime(value) {
  const [hours = '0', minutes = '00'] = String(value || '').split(':');
  return `${Number(hours)}:${minutes}`;
}

function submitSearch() {
  if (!validateForm()) return;

  submitError.value = '';
  submitting.value = true;
  document.getElementById('loader-page')?.classList.remove('d-none');

  const dropOffStation = isOtherReturnPlace.value ? selectedReturnOffice.value : selectedPickupOffice.value;
  const params = {
    'PickUpStation.Name': getOfficeLabel(selectedPickupOffice.value),
    'PickUpStation.Id': getOfficeId(selectedPickupOffice.value),
    'PickUpStation.ExternalId': getOfficeExternalId(selectedPickupOffice.value),
    'PickUpStation.Type': getOfficeType(selectedPickupOffice.value),
    SameStation: isOtherReturnPlace.value ? 'False' : 'True',
    'DropOffStation.Name': getOfficeLabel(dropOffStation),
    'DropOffStation.Id': getOfficeId(dropOffStation),
    'DropOffStation.ExternalId': getOfficeExternalId(dropOffStation),
    'DropOffStation.Type': getOfficeType(dropOffStation),
    StartDate: dayjs(checkIn.value).format('YYYY/MM/DD'),
    PickUpTime: formatWidgetTime(form.value.pickupTime),
    EndDate: dayjs(checkOut.value).format('YYYY/MM/DD'),
    DropOffTime: formatWidgetTime(form.value.returnTime),
    SourceHost: '',
    SiteType: 'None',
    'Searchbox.PostUrl': '',
    'Searchbox.IsModal': 'False',
    'Searchbox.FormMethod': 'Post',
    WhiteLabelId: '',
  };

  const carsSubmitUrl = config.endPoints.carsSubmitUrl
    || config.endPoints.transferSubmitUrl?.replace(/transferSubmit$/i, 'carSubmit');

  jsonp(carsSubmitUrl, params, { callbackParam: 'jsoncallback' })
    .then((response) => {
      if (response?.Errors?.length) {
        submitError.value = response.Errors.join(' ');
        document.getElementById('loader-page')?.classList.add('d-none');
        return;
      }
      navigateToResults(response);
    })
    .catch((error) => {
      console.log(error);
      submitError.value = __('booker.cars_submit_error');
      document.getElementById('loader-page')?.classList.add('d-none');
    })
    .finally(() => {
      submitting.value = false;
    });
}

function jsonp(url, params, { callbackParam = 'callback', timeoutMs = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('cars submit URL is not configured'));
      return;
    }

    const callbackName = `__carsSubmitCallback_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const script = document.createElement('script');
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      delete window[callbackName];
      script.remove();
    };

    window[callbackName] = (data) => {
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      cleanup();
      reject(new Error('jsonp request failed'));
    };
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('jsonp request timed out'));
    }, timeoutMs);

    const query = new URLSearchParams({ ...params, [callbackParam]: callbackName }).toString();
    script.src = `${url}?${query}`;
    document.body.appendChild(script);
  });
}

function navigateToResults(response) {
  if (!response?.Url) {
    throw new Error('cars submit response does not contain a results URL');
  }

  const formEl = document.createElement('form');
  formEl.setAttribute('method', response.Method === 1 ? 'post' : 'get');
  const resultsUrl = /^https?:\/\//i.test(response.Url)
    ? response.Url
    : `${config.tourSiteUrl.replace(/\/$/, '')}/${response.Url.replace(/^\//, '')}`;
  formEl.setAttribute('action', resultsUrl);

  Object.entries(response.FormData || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', value);
    formEl.appendChild(hiddenField);
  });

  document.body.appendChild(formEl);
  formEl.submit();
}

onMounted(() => {
  initializeDefaultDates();
  updateFormDates();
});
</script>

<template>
  <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.cars')">
    <div class="search__inputs" :class="{ 'search__inputs--other-return': isOtherReturnPlace }">
      <SmartBox :title="__('booker.pickup_office_sub')" :OpenBox="openPickupOfficeSearch" :isHeaderActive="false"
        :hasContent="pickupOfficeResults.length > 0 || showEmptyPickupOffice" @update:OpenBox="openPickupOfficeSearch = $event"
        @isMobile="isMobile = $event" @open="onOpenPickupOfficeSearch">
        <template #trigger>
          <SearchInput id="cars-pickup-office" :label="__('booker.pickup_office')" :selectOnFocus="!isMobile"
            :activeFocus="!isMobile" :placeholder="__('booker.pickup_office_placeholder')" v-model="form.pickupOffice"
            @input="onSearchPickupOffice" :debounce="500" :autocomplete="'off'" :spellcheck="false"
            :error="!!fieldErrors.pickupOffice" :errorMessage="fieldErrors.pickupOffice" :readonly="isMobile">
            <template #icon>
              <span class="icon font-icons icons-location font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <div class="searcher">
            <div v-if="isMobile" class="searcher__head">
              <SearchInput ref="pickupOfficeMobileInput" id="cars-pickup-office-mobile" :label="__('booker.pickup_office')"
                :selectOnFocus="true" :activeFocus="true" :placeholder="__('booker.pickup_office_placeholder')"
                v-model="form.pickupOffice" @input="onSearchPickupOffice($event, true)" :debounce="500" :autocomplete="'off'"
                :spellcheck="false">
                <template #icon>
                  <span class="icon font-icons icons-location font-20"></span>
                </template>
              </SearchInput>
            </div>
            <ul v-if="pickupOfficeResults.length > 0" class="searcher__list">
              <li v-for="(item, index) in pickupOfficeResults" :key="item.id || item.code || `pickup-office-${index}`"
                class="searcher__item">
                <button class="searcher__action" type="button" @click="onSelectPickupOffice(item)">
                  <span class="searcher__icon icons-location font-20" aria-hidden="true"></span>
                  <span class="searcher__label">{{ getOfficeLabel(item) }}</span>
                </button>
              </li>
            </ul>
            <SearchEmpty v-else-if="showEmptyPickupOffice" :title="__('booker.no_results_title')"
              :text="__('booker.no_results_hint', [form.pickupOffice])" />
          </div>
        </template>
      </SmartBox>

      <SmartBox v-if="isOtherReturnPlace" :title="__('booker.return_office_sub')" :OpenBox="openReturnOfficeSearch"
        :isHeaderActive="false" :hasContent="returnOfficeResults.length > 0 || showEmptyReturnOffice"
        @update:OpenBox="openReturnOfficeSearch = $event" @isMobile="isMobile = $event"
        @open="onOpenReturnOfficeSearch">
        <template #trigger>
          <SearchInput id="cars-return-office" :label="__('booker.return_office')" :selectOnFocus="!isMobile"
            :activeFocus="!isMobile" :placeholder="__('booker.return_office_placeholder')" v-model="form.returnOffice"
            @input="onSearchReturnOffice" :debounce="500" :autocomplete="'off'" :spellcheck="false"
            :error="!!fieldErrors.returnOffice" :errorMessage="fieldErrors.returnOffice" :readonly="isMobile">
            <template #icon>
              <span class="icon font-icons icons-location font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <div class="searcher">
            <div v-if="isMobile" class="searcher__head">
              <SearchInput ref="returnOfficeMobileInput" id="cars-return-office-mobile" :label="__('booker.return_office')"
                :selectOnFocus="true" :activeFocus="true" :placeholder="__('booker.return_office_placeholder')"
                v-model="form.returnOffice" @input="onSearchReturnOffice($event, true)" :debounce="500" :autocomplete="'off'"
                :spellcheck="false">
                <template #icon>
                  <span class="icon font-icons icons-location font-20"></span>
                </template>
              </SearchInput>
            </div>
            <ul v-if="returnOfficeResults.length > 0" class="searcher__list">
              <li v-for="(item, index) in returnOfficeResults" :key="item.id || item.code || `return-office-${index}`"
                class="searcher__item">
                <button class="searcher__action" type="button" @click="onSelectReturnOffice(item)">
                  <span class="searcher__icon icons-location font-20" aria-hidden="true"></span>
                  <span class="searcher__label">{{ getOfficeLabel(item) }}</span>
                </button>
              </li>
            </ul>
            <SearchEmpty v-else-if="showEmptyReturnOffice" :title="__('booker.no_results_title')"
              :text="__('booker.no_results_hint', [form.returnOffice])" />
          </div>
        </template>
      </SmartBox>

      <div class="dateField">
        <SmartBox ref="datesBox" :title="__('booker.dates')" :isHeaderActive="false" :mobile-breakpoint="768">
          <template #trigger>
            <SearchInput id="calendar-cars" type="text" required name="calendar-cars" :activeFocus="true"
              :label="__('booker.dates')" :readonly="true" v-model="form.dates" :error="!!fieldErrors.dates"
              :errorMessage="fieldErrors.dates">
              <template #icon>
                <span class="icon font-icons icons-calendar-depart font-20"></span>
              </template>
            </SearchInput>
          </template>
          <template #content>
            <div class="datesTimes">
              <BookerCalendar :modelValue="calendarDates" :currency="config.currency" :mode="'range'" :unit="'days'"
                :minDays="1" :minDate="defaultDate" :startLabel="__('booker.pickup_date')"
                :endLabel="__('booker.return_date')" @select="onSelectDates" @close="datesBox.close()" />
            </div>
          </template>
        </SmartBox>
      </div>
      <div class="datesTimes__hours">
        <label class="datesTimes__hour">
          <span class="datesTimes__icon icon font-icons icons-clock font-20"></span>
          <span class="datesTimes__data">
            <span class="datesTimes__label font-12 font-500 font-strong">{{ __('booker.pickup_time') }}</span>
            <select class="datesTimes__select font-16 font-500 font-main" v-model="form.pickupTime">
              <option v-for="slot in TIME_SLOTS" :key="`pickup-${slot}`" :value="slot">{{ slot }}</option>
            </select>
          </span>
        </label>
        <label class="datesTimes__hour">
          <span class="datesTimes__icon icon font-icons icons-clock font-20"></span>
          <span class="datesTimes__data">
            <span class="datesTimes__label font-12 font-500 font-strong">{{ __('booker.return_time') }}</span>
            <select class="datesTimes__select font-16 font-500 font-main" v-model="form.returnTime">
              <option v-for="slot in TIME_SLOTS" :key="`return-${slot}`" :value="slot">{{ slot }}</option>
            </select>
          </span>
        </label>
      </div>
    </div>

    <div class="search__filters">
      <div class="group">
        <legend class="group__legend font-14 font-400 font-strong">
          {{ __('booker.return_type') }}
        </legend>
        <ul class="group__list">
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="returnMode"
                :value="RETURN_MODES.SAME" v-model="form.returnMode" v-on:change="onChangeReturnMode" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">{{ __('booker.same_place') }}</span>
            </label>
          </li>
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="returnMode"
                :value="RETURN_MODES.OTHER" v-model="form.returnMode" v-on:change="onChangeReturnMode" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">{{ __('booker.other_place') }}</span>
            </label>
          </li>
        </ul>
      </div>
    </div>

    <div class="search__action">
      <button type="submit" class="search__btn btnPrimary btnPrimary--md font-16 font-500" :disabled="submitting">
        {{ __('booker.button') }}
      </button>
      <p v-if="submitError" class="search__error font-14">{{ submitError }}</p>
    </div>
  </form>
</template>

<style scoped lang="scss">
.search {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  padding: var(--space-16);
  border-radius: var(--space-12);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  background-color: var(--bg-base);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: 100%;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    display: grid;
    grid-template-areas:
      'inputs inputs'
      'filters action';
    grid-template-columns: 1fr auto;
    padding-inline: 0px;
    gap: var(--space-16) var(--space-24);
    box-shadow: none;
    margin: 0px auto;
    border: 0px;
  }

  @media (min-width: 1280px) {
    grid-template-areas:
      'inputs action'
      'filters filters';
    grid-template-columns: minmax(0, 1fr) 160px;
    width: 100%;
    margin: 0px auto;
    padding: var(--space-20);
    border-radius: var(--space-12);
    row-gap: var(--space-12);
    column-gap: var(--space-32);
    border: 1px solid var(--border-subtle);
    box-shadow: var(--shadow-300);
  }

  .icon {
    display: flex;
  }

  &__filters {
    grid-area: filters;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-16);
    order: 1;

    @media (min-width: 768px) {
      justify-content: center;
      order: initial;
    }

    @media (min-width: 1280px) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-24);
    }
  }

  &__inputs {
    grid-area: inputs;
    display: flex;
    flex-direction: column;
    gap: var(--space-16);
    order: 2;

    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: end;
      gap: var(--space-16);
      order: initial;
    }

    @media (min-width: 1280px) {
      grid-template-columns:
        minmax(0, 3fr)
        minmax(0, 3fr)
        minmax(0, 4fr);
      gap: var(--space-20, 20px);
    }

    &--other-return {
      @media (min-width: 1280px) {
        grid-template-columns:
          minmax(0, 2fr)
          minmax(0, 2fr)
          minmax(0, 2fr)
          minmax(0, 4fr);
      }
    }
  }

  &__action {
    grid-area: action;
    order: 3;

    @media (min-width: 768px) {
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
      align-self: start;
      order: initial;
    }

    @media (min-width: 1280px) {
      align-self: stretch;
      align-items: flex-start;
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 768px) {
      width: 160px;
    }

    @media (min-width: 1280px) {
      width: 100%;
    }
  }

  &__error {
    color: var(--text-error, #950000);
    margin: var(--space-8) 0 0;
  }
}

.group {
  border: none;
  padding: 0px;
  margin: 0px;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-12);
  }

  &__legend {
    display: flex;
    margin: 0px 0px var(--space-8);

    @media (min-width: 768px) {
      display: flex;
      width: fit-content;
      margin: 0px;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-12);
    margin: 0px;
    padding: 0px;
    list-style: none;
  }
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  margin: 0px;
}

.searcher {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  background-color: var(--bg-base);
  width: 100%;

  @media (min-width: 768px) {
    padding: var(--space-16);
    border-radius: var(--space-16);
    width: calc(100vw - 32px);
    max-width: 460px;
  }

  &__head {
    padding-bottom: var(--space-20);
    border-bottom: 1px solid var(--border-subtle);

    @media (min-width: 768px) {
      display: none;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  &__item {
    width: 100%;
    border-bottom: 1px solid var(--border-subtle);
  }

  &__action {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-8);
    border-radius: var(--space-8);
    gap: var(--space-16);
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--bg-level2);
    }
  }

  &__icon {
    width: 35px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: var(--space-8);
  }

  &__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.dateField,
.datesTimes {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.dateField {
  gap: var(--space-10, 10px);
}

.datesTimes {

  @media (min-width: 768px) {
    width: 750px;
  }

  &__hours {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-16);

    @media (min-width: 768px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-20);
    }

    @media (min-width: 768px) and (max-width: 1279px) {
      grid-column: 1 / -1;
    }

    @media (min-width: 1280px) {
      width: 100%;
    }
  }

  &__hour {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    height: 52px;
    padding: 0px var(--space-12);
    border: 1px solid var(--border-strong);
    border-radius: var(--space-8);
    background-color: var(--bg-base);
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
    margin: 0px;

    @media (hover: hover) {
      &:hover {
        border-color: var(--border-strong-hover);
      }
    }

    &:focus-within {
      border-color: transparent;
      box-shadow: 0 0 0 2px var(--border-info);
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-main);
  }

  &__data {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
  }

  &__label {
    padding-top: calc(var(--space-4) * 1.5);
    pointer-events: none;
  }

  &__select {
    width: 100%;
    height: 26px;
    padding: 0px;
    border: none;
    outline: none;
    background: transparent;
    cursor: pointer;
    text-overflow: ellipsis;
    appearance: none;
    -webkit-appearance: none;
  }
}
</style>
